import { type SiteResponse, SiteType, v1SitesList } from '@on3/api/src';
import { SelectSearch } from '@on3/ui-lib/components/SelectSearch';
import { externalApi } from '@on3/ui-lib/index';
import { SiteKeys } from '@on3/ui-lib/utils/constants';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import { createTitle } from '@public/utils/meta';
import { type Dispatch, useEffect, useState } from 'react';

interface TeamSelectProps {
  className?: string;
  selectedSite: SiteResponse | null;
  setSelectedSite: Dispatch<SiteResponse | null>;
}

export const TeamSelect = ({
  className,
  selectedSite: _selectedSite,
  setSelectedSite,
}: TeamSelectProps) => {
  const [sites, setSites] = useState<SiteResponse[]>([]);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const data = await v1SitesList(externalApi, {});

        setSites(data);
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };

    fetchSites();
  }, []);

  const options = sites
    .filter((s) => s.type === SiteType.Team || s.type === SiteType.Channel)
    // filter all channel sites that have a sibling key
    .filter((s) =>
      s.type === SiteType.Channel && s.siblingSiteKey ? false : true,
    )
    // special filtering logic
    .filter((s) => {
      // exclude site keys 1372, 1373, 1374
      const excludedKeys = [
        SiteKeys.KSR,
        SiteKeys.CatsIllustrated,
        SiteKeys.MaizeAndBlueReview,
        SiteKeys.InsideNebraska,
        SiteKeys.OUInsider,
      ];

      return !excludedKeys.includes(s.key);
    })
    .map((s) => ({
      ...s,
      label: !s.organization
        ? s.name || s.siteName || ''
        : createTitle(
            s.organization?.knownAs || s.organization?.name,
            s.organization?.mascot,
            [74, 453].includes(s.key) ? `- ${s.name || s.siteName}` : '',
          ),
      avatarUrl: getAssetUrl(s.organization?.defaultAssetRes),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedSite =
    options.find((s) => s.key === _selectedSite?.key) || null;

  return (
    <section className={className}>
      <header>
        <h2>Select your team</h2>
      </header>
      <SelectSearch
        label="Search teams..."
        onChange={setSelectedSite}
        options={options}
        value={selectedSite}
      />
    </section>
  );
};
